import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FieldOption } from './FormField';
import { useDropdownPosition } from '../hooks/useDropdownPosition';
import { useClickOutside } from '../hooks/useClickOutside';
import '../styles/components/CustomDropdown.scss';

interface CustomDropdownProps {
    options: FieldOption[];
    value: string; // This will be the actual value (e.g., 'ingredients')
    onChange: (value: string) => void; // Should be called with the value of selected/typed item
    placeholder: string;
    required: boolean;
    allowCustomText?: boolean; // New prop to explicitly allow submitting custom text
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
    options,
    value, // Actual current value
    onChange,
    placeholder,
    required,
    allowCustomText = false, // Default to false, meaning selection from list is preferred/enforced by parent
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(''); // For display and typing in input
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const optionsContainerRef = useRef<HTMLDivElement>(null);

    // Use custom hooks for positioning and outside clicks
    const dropdownPosition = useDropdownPosition({
        isOpen,
        triggerRef,
        alignment: 'left',
        offsetY: 4,
    });

    useClickOutside({
        refs: [triggerRef, optionsContainerRef],
        isOpen,
        onClickOutside: () => {
            setIsOpen(false);
            // If custom text is not allowed, and the dropdown is closed by clicking outside,
            // reset inputValue to reflect the actual selected value's label,
            // effectively discarding any unsubmitted typed text.
            if (!allowCustomText) {
                const currentSelectedOption = options.find(
                    (opt) => opt.value === value,
                );
                setInputValue(
                    currentSelectedOption ? currentSelectedOption.label : '',
                );
            }
            // If allowCustomText is true, inputValue is managed by its own useEffect
            // or direct calls to setInputValue, and shouldn't be reset here
            // as it might contain valid custom text.
        },
    });

    useEffect(() => {
        // Sync inputValue with the label of the current value, or if value is custom text itself
        const selectedOption = options.find((option) => option.value === value);
        if (selectedOption) {
            setInputValue(selectedOption.label);
        } else if (value && allowCustomText) {
            setInputValue(value); // If value is not in options, it might be custom text
        } else {
            setInputValue('');
        }
    }, [value, options, allowCustomText]);

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const typedText = e.target.value;
        setInputValue(typedText);
        setIsOpen(true);
        if (allowCustomText) {
            onChange(typedText); // If custom text is allowed, propagate it immediately
        } else {
            // If not allowing custom, don't call onChange with typed text, only with selected option value
            // But we might want to clear the actual `value` if the text doesn't match any option
            const matchingOption = options.find(
                (opt) => opt.label.toLowerCase() === typedText.toLowerCase(),
            );
            if (!matchingOption) {
                // onChange(''); // Optional: clear value if text doesn't match any option label, forcing re-selection
            }
        }
    };

    const handleSelectOption = (option: FieldOption) => {
        setInputValue(option.label);
        onChange(option.value);
        setIsOpen(false);
    };

    const toggleDropdown = () => {
        if (isOpen && !allowCustomText) {
            const currentSelectedOption = options.find(
                (opt) => opt.value === value,
            );
            setInputValue(
                currentSelectedOption ? currentSelectedOption.label : '',
            );
        }
        setIsOpen(!isOpen);
    };

    return (
        <div className="custom-dropdown" ref={dropdownRef}>
            <div
                className={`dropdown-input-wrapper ${isOpen ? 'open' : ''}`}
                ref={triggerRef}
            >
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onClick={() => setIsOpen(true)} // Open on click as well
                    placeholder={placeholder}
                    className="dropdown-input"
                />
                {required && (
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`dropdown-chevron ${isOpen ? 'open' : ''}`}
                        onClick={toggleDropdown}
                    />
                )}
            </div>

            {isOpen &&
                (filteredOptions.length > 0 || !allowCustomText) &&
                ReactDOM.createPortal(
                    <div
                        ref={optionsContainerRef}
                        className="custom-dropdown-options"
                        style={{
                            position: 'absolute',
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`,
                            minWidth: triggerRef.current
                                ? `${triggerRef.current.offsetWidth}px`
                                : 'auto',
                            zIndex: 1000,
                        }}
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    className={`dropdown-option ${value === option.value ? 'selected' : ''}`}
                                    onClick={() => handleSelectOption(option)}
                                >
                                    <span>{option.label}</span>
                                    {value === option.value && (
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="check-icon"
                                        />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-option no-options">
                                No matching options
                            </div>
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default CustomDropdown;
